import { useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"
import TabMenu from "src/shared/components/navigation/tab-menu/TabMenu"
import FlexibleTable, { FlexibleTableRef } from "src/shared/components/data-display/FlexibleTable/FlexibleTable"
import { v4 as uuid } from "uuid"
import cx from "classnames"
import "./Table.css"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "src/store/store"
import { toggleDrawer } from "src/store/navigation/navigationSlice"
import { tabs, tabsValet } from "../../Inicio.constants"
import { AddButton } from "../../components/AddButton/AddButton"
import { Search } from "../../components/Search/Search"
import { Option } from "src/shared/components/forms/selelct-dropdown/Dropdown"
import {
    EstadosReservas,
    EstadoPago,
    GetReservacionesTableQuery,
    TiposPagos,
    useGetReservacionesTableQuery,
    useGetTiposHabitacionQuery,
} from "src/gql/schema"
import { formatCurrency } from "src/shared/hooks/formatCurrency"
import { iLikeText } from "src/shared/helpers/ILikeText"
import { DrawerSection } from "../Drawer/Drawer"
import { useDate } from "src/shared/hooks/useDate"
import EmptyState from "src/shared/components/layout/empty-state/EmptyState"
import useSortTable from "src/shared/hooks/useSortTable"
import Icon from "src/shared/icons"
import { selectDrawerSection, selectReservation } from "src/store/reservations/reservationsSlice"
// import LoaderComponent from "src/shared/components/layout/loader/Loader"
import { Skeleton } from "../../components/Skeleton/Skeleton"
import { useProfile } from "src/shared/hooks/useProfile"
import { getOrigenLabel } from "src/pages/reservaciones/helpers/origen"
import Badge from "src/shared/components/data-display/Badge/Badge"
import { RoleNames } from "src/shared/hooks/useAuth"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
import { CalendarButtons } from "src/shared/components/navigation/CalendarButtons/CalendarButtons"
import { PAYMENT_METHODS } from "src/constants/payments"

const options: Option[] = [
    { label: "Enero", value: "0" },
    { label: "Febrero", value: "1" },
    { label: "Marzo", value: "2" },
    { label: "Abril", value: "3" },
    { label: "Mayo", value: "4" },
    { label: "Junio", value: "5" },
    { label: "Julio", value: "6" },
    { label: "Agosto", value: "7" },
    { label: "Septiembre", value: "8" },
    { label: "Octubre", value: "9" },
    { label: "Noviembre", value: "10" },
    { label: "Diciembre", value: "11" },
]


export const TableSection = () => {
    const { hotel_id, rolName } = useProfile()
    const { isDrawerOpen } = useSelector((state: RootState) => state.navigation)
    const { formatCustomDate, nowHotel, getTimezone, isSameHotelDay } = useFormatDate()
    const zonehotel = getTimezone()
    // "Ahora" reproyectado a la zona del hotel
    const nowInHotelZone = DateTime.fromJSDate(nowHotel).setZone(zonehotel)
    const firstDayOfMonth = nowInHotelZone.startOf("month").toJSDate()
    const lastDayOfMonth = nowInHotelZone.endOf("month").toJSDate()
    const [showGradient, setShowGradient] = useState(false)

    const { data, refetch, loading } = useGetReservacionesTableQuery({
        variables: {
            id: [],
            hotel_id,
            fecha_entrada: {
                fecha_inicial: firstDayOfMonth,
                fecha_final: lastDayOfMonth,
            },
        },
        fetchPolicy: "no-cache",
    })

    const startOfDay = nowInHotelZone.startOf("day").toJSDate()
    const endOfDay = nowInHotelZone.endOf("day").toJSDate()

    const useReservacionesQuery = (fechaInicial, fechaFinal) => {
        return useGetReservacionesTableQuery({
            variables: {
                id: [],
                hotel_id,
                fecha_entrada: {
                    fecha_inicial: fechaInicial,
                    fecha_final: fechaFinal,
                },
            },
            fetchPolicy: "no-cache",
        })
    }

    const { data: dataDiaActual, refetch: refetchDiaActual } = useReservacionesQuery(startOfDay, endOfDay)
    const { data: dataMesActual, refetch: refetchMesActual } = useReservacionesQuery(firstDayOfMonth, lastDayOfMonth)

    const { setHHMMSS, UTCStringToLocalDate } = useDate()
    const [date, setDate] = useState<number>(nowHotel.getMonth())
    const [year, setYear] = useState<number>(nowHotel.getFullYear())
    const [filterLoading, setFilterLoading] = useState<boolean>(false)

    const headers = tableItems()

    const tabsToUse = rolName === RoleNames.valet ? tabsValet : tabs
    const [path, setPath] = useState<string>(tabsToUse[0].path)

    const [searchValue] = useState<string>("")
    const [selectReserva, setSelectReserva] = useState<string>("")
    const tableRef = useRef<FlexibleTableRef>(null)
    // const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null) // usado por la búsqueda vía backend (master), comentada más abajo

    const [reservasQuery, setReservasQuery] = useState<GetReservacionesTableQuery["reservas"]>([])
    // Todas las reservas formateadas para la tabla (este array no se muestra directamente, es para tener una referencia a todas las reservas y hacerle filtros)
    const [dataTables, setDataTables] = useState<
        {
            goTo: string
            value: {
                value: JSX.Element | string
                sortValue?: string
                fromHeaderSort?: string
                filterValue?: string
            }[]
        }[]
    >([])

    // las reservas filtradas para ser mostradas (este array se muestra directamente en la tabla)
    const [dataTable, setDataTable] = useState<
        {
            goTo: string
            value: {
                value: JSX.Element | string
                sortValue?: string
                fromHeaderSort?: string
                filterValue?: string
            }[]
        }[]
    >(dataTables)
    // las reservas filtradas para ser mostradas sin ordenamiento, (para regresar al estado orginal al cancelar el ordenamiento)
    const [startDataTable, setStartDataTable] = useState(dataTable)

    useEffect(() => {
        if (data) {
            setReservasQuery(data.reservas || [])
        }
    }, [data])

    useEffect(() => {
        const now = nowHotel
        setDataTables(
            reservasQuery?.map((reserva) => ({
                goTo: reserva?.reserva_id,
                value: [
                    {
                        value: reserva?.codigo_ota
                            ? reserva?.codigo_ota
                            : `ER-${reserva?.folio.toString().padStart(3, "0")}`,
                    },
                    {
                        value: formatTableStateAsignacion({
                            idHabitacion: reserva?.habitacion_id || "",
                            numHabitacion: reserva?.habitacion?.numero_habitacion || "",
                        }),
                        fromHeaderSort: headers[0].value,
                        sortValue: reserva?.habitacion?.numero_habitacion || "",
                    },
                    { value: reserva?.origen ? getOrigenLabel(reserva?.origen) : "" },
                    { value: reserva?.tipo_de_habitacion?.nombre || "" },
                    {
                        value: <div className="reservas-screen__table-cell__huesped">{reserva?.nombre_huesped}</div>,
                    },

                    {
                        value: `${formatCustomDate(
                            reserva?.fecha_entrada,
                            "DD/MMM/YY"
                        )} - ${formatCustomDate(reserva?.fecha_salida, "DD/MMM/YY")}`,
                        fromHeaderSort: headers[4].value,
                        sortValue: reserva?.fecha_entrada,
                    },

                    {
                        value: getTarifaLabel(reserva)
                    },
                    {
                        value: formatMetodoPago(reserva?.pagos),
                        filterValue: getMetodoPagoLabel(reserva?.pagos),
                    },
                    {
                        value: formatTableState({
                            statePago: reserva?.estado_pago,
                            fechaCancel: reserva?.fecha_cancelacion || "",
                            isNoShow:
                                reserva?.estado !== EstadosReservas.CheckIn &&
                                now.getTime() >
                                setHHMMSS({
                                    startDate: UTCStringToLocalDate(reserva?.fecha_salida),
                                    newHour: reserva?.reservas_tarifas?.slice().sort((a, b) => new Date(b.fecha_fin).getTime() - new Date(a.fecha_fin).getTime())?.[0]?.tarifa?.hora_checkout || "",
                                }).getTime() +
                                60 * 1000,
                        }),
                        filterValue: getPagoLabel({
                            statePago: reserva?.estado_pago,
                            fechaCancel: reserva?.fecha_cancelacion || "",
                            isNoShow:
                                reserva?.estado !== EstadosReservas.CheckIn &&
                                now.getTime() >
                                setHHMMSS({
                                    startDate: UTCStringToLocalDate(reserva?.fecha_salida),
                                    newHour: reserva?.reservas_tarifas?.slice().sort((a, b) => new Date(b.fecha_fin).getTime() - new Date(a.fecha_fin).getTime())?.[0]?.tarifa?.hora_checkout || "",
                                }).getTime() +
                                60 * 1000,
                        }),
                    },
                ],
            }))
        )
    }, [reservasQuery])

    const dispatch = useDispatch()

    useEffect(() => {
        setDataTable(dataTables)
        setStartDataTable(dataTables)
    }, [dataTables])

    const [tabsValue, setTabsValue] = useState<
        {
            label: string
            number: number
            path: string
        }[]
    >([])

    useEffect(() => {
        const tabsList: {
            label: string
            number: number
            path: string
        }[] = []
        tabsToUse.map((v) => {
            if (v.path === "/all") {
                tabsList.push({
                    label: v.label,
                    path: v.path,
                    number: data?.reservas.length || 0,
                })
            }
            if (v.path === "/current") {
                tabsList.push({
                    label: v.label,
                    path: v.path,
                    number: dataDiaActual?.reservas.length || 0,
                })
            }
            if (v.path === "/currentmonth" && rolName === RoleNames.valet) {
                tabsList.push({
                    label: v.label,
                    path: v.path,
                    number: dataMesActual?.reservas.length || 0,
                })
            }
        })
        setTabsValue(tabsList)
    }, [data, dataDiaActual, dataMesActual, rolName])

    const searchFilter = (value) => {
        let reservas = data?.reservas
        reservas =
            path === "/current"
                ? dataDiaActual?.reservas.filter((res) => isSameHotelDay(res.fecha_entrada, nowHotel))
                : path === "/currentmonth" && rolName === RoleNames.valet
                    ? dataMesActual?.reservas.filter((res) => {
                        const fechaEntrada = new Date(res.fecha_entrada)
                        return fechaEntrada >= firstDayOfMonth && fechaEntrada <= lastDayOfMonth
                    })
                    : data?.reservas

        if (!value) {
            return setReservasQuery(reservas || [])
        }

        setReservasQuery(
            reservas?.filter((res) => {
                return (
                    iLikeText({ fullText: res.nombre_huesped, searchText: value }) ||
                    res.habitacion.numero_habitacion === value
                )
            }) || []
        )
    }

    useEffect(() => {
        if (path === "/all") {
            if (data) {
                const tableNew = data?.reservas
                if (tableNew) {
                    setReservasQuery(tableNew)
                }
            }
        } else if (path === "/current") {
            if (data) {
                const dataTableFilter = dataDiaActual?.reservas || []
                if (dataTableFilter) {
                    setReservasQuery(dataTableFilter)
                }
            }
        } else if (path === "/currentmonth") {
            if (data) {
                const dataTableFilter = dataMesActual?.reservas || []
                if (dataTableFilter) {
                    setReservasQuery(dataTableFilter)
                }
            }
        }
    }, [path, data, dataDiaActual, dataMesActual])

    const toggleDrawerState = (value: boolean) => {
        dispatch(selectDrawerSection("detail"))
        dispatch(toggleDrawer(value))
    }

    useEffect(() => {
        if (isDrawerOpen) {
            return
        }
        const fecha_inicial =
            path === "/current"
                ? startOfDay
                : path === "/currentmonth"
                    ? DateTime.fromObject({ year, month: nowInHotelZone.month, day: 1 }, { zone: zonehotel })
                        .startOf("day")
                        .toJSDate()
                    : DateTime.fromObject({ year, month: date + 1, day: 1 }, { zone: zonehotel })
                        .startOf("day")
                        .toJSDate()

        const fecha_final =
            path === "/current"
                ? endOfDay
                : path === "/currentmonth"
                    ? DateTime.fromObject({ year, month: nowInHotelZone.month }, { zone: zonehotel })
                        .endOf("month")
                        .toJSDate()
                    : DateTime.fromObject({ year, month: date + 1 }, { zone: zonehotel }).endOf("month").toJSDate()

        if (path === "/current") {
            refetchDiaActual({
                id: [],
                hotel_id,
                fecha_entrada: {
                    fecha_inicial,
                    fecha_final,
                },
            })
        } else if (path === "/currentmonth") {
            refetchMesActual({
                id: [],
                hotel_id,
                fecha_entrada: {
                    fecha_inicial,
                    fecha_final,
                },
            })
        } else if (path === "/all") {
            refetch({
                id: [],
                hotel_id,
                fecha_entrada: {
                    fecha_inicial,
                    fecha_final,
                },
            })
        }
    }, [isDrawerOpen, path, year, date, refetch, refetchDiaActual, refetchMesActual])

    useEffect(() => {
        setFilterLoading(true)
        const fecha_inicial = DateTime.fromObject({ year, month: date + 1, day: 1 }, { zone: zonehotel })
            .startOf("day")
            .toJSDate()
        const fecha_final = DateTime.fromObject({ year, month: date + 1 }, { zone: zonehotel })
            .endOf("month")
            .toJSDate()
        refetch({
            id: [],
            hotel_id,
            fecha_entrada: {
                fecha_inicial: fecha_inicial,
                fecha_final: fecha_final,
            },
        })
        setTimeout(() => setFilterLoading(false), 1000)
    }, [date, year])

    const [onFilterTable, setOnFilterTable] = useState<
        {
            filter: string
            fromHeader: string
            idx: number
        }[]
    >([])

    // filtrar reservas
    useEffect(() => {
        if (!onFilterTable.length) {
            setStartDataTable(dataTables)
            setDataTable(dataTables)
            return
        }

        const filtersByColumn = onFilterTable.reduce<Record<number, string[]>>((acc, { idx, filter }) => {
            acc[idx] ??= []
            acc[idx].push(filter)
            return acc
        }, {})

        const filteredData = dataTables.filter((row) =>
            Object.entries(filtersByColumn).every(([idx, filters]) => {
                const cell = row.value[Number(idx)]
                const cellValue = cell?.filterValue ?? (cell?.value as string)
                return filters.includes(cellValue)
            })
        )

        setStartDataTable(filteredData)
        setDataTable(filteredData)
    }, [onFilterTable, dataTables])

    const getActiveData = () =>
        path === "/current" ? dataDiaActual
        : path === "/currentmonth" ? dataMesActual
        : data

    const drawerCreate = (id: string) => {
        setSelectReserva(id)
        dispatch(selectReservation(getActiveData()?.reservas.find((r) => r.reserva_id === id)))
        toggleDrawerState(true)
    }

    const onSortData = (value: { sort: "up" | "down" | null; fromHeader: string; idx: number } | null) => {
        if (!value) {
            return
        }
        setDataTable(
            useSortTable({ i: value.idx, sortedData: dataTable, startList: startDataTable, sortState: value.sort })
        )
    }
    const getModalidad = (t) =>
        t?.tarifa?.grupo_tarifa?.modalidad?.toLowerCase?.() || ""

    const getTarifaLabel = (reserva) => {
        const tarifas = reserva?.reservas_tarifas || []
        const total = formatCurrency(reserva?.total || 0)

        if (!tarifas.length) return `Sin tarifa - ${total}`

        const tarifasFiltered = tarifas.filter((tarifa, index, tarifasList) =>
            tarifasList.findIndex((item) => item.tarifa_id === tarifa.tarifa_id) === index
        )

        const base = tarifasFiltered.filter(t => getModalidad(t) === "base")
        const temporada = tarifasFiltered.filter(t => getModalidad(t) === "temporada")
        const adicionales = tarifasFiltered.filter(t => getModalidad(t) === "adicional")

        const pluralize = (n, word) => `${n} ${n === 1 ? word : word + "s"}`

        // tarifa única
        if (tarifasFiltered.length === 1) {
            const modalidad = getModalidad(tarifasFiltered[0])
            return `Tarifa ${modalidad} - ${total}`
        }

        // tarifas adicionales
        if (adicionales.length === tarifasFiltered.length) {
            return `Tarifas adicionales (${tarifasFiltered.length}) - ${total}`
        }

        // tarifas temporada
        if (temporada.length === tarifasFiltered.length) {
            return `Tarifas temporada (${tarifasFiltered.length}) - ${total}`
        }

        // Tarifa base + otras
        if (base.length === 1) {
            const extraCount = tarifasFiltered.length - 1
            return `Tarifa base + (${pluralize(extraCount, "tarifa")}) - ${total}`
        }

        // Sin base
        return `(${tarifasFiltered.length}) Tarifas - ${total}`
    }
    return (
        <section className="reservas-screen__table-section">
            {loading || filterLoading ? (
                <div>
                    <div className="reservas-screen__table__filter-section">
                        <TabMenu
                            className="reservas-screen__table__tabs"
                            tabList={tabsValue}
                            value={path}
                            showNumerOnNoItems
                            onChange={(value) => {
                                setPath(value)
                            }}
                        />
                        <div className="reservas-screen__table__search-section">
                            <Search onChange={searchFilter} />
                            {path === "/all" && !searchValue && (
                                <>
                                    <CalendarButtons
                                        className="gastos-screen-header-controls"
                                        currMonth={date}
                                        setCurrMonth={setDate}
                                        currYear={year}
                                        setCurrYear={setYear}
                                        monthNames={options.map(({ label }) => label)}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    <div className="reservas-screen__table">
                        <Skeleton />
                    </div>
                </div>
            ) : data?.reservas.length !== 0 ? (
                <div>
                    <div className="reservas-screen__table__filter-section">
                        <TabMenu
                            className="reservas-screen__table__tabs"
                            tabList={tabsValue}
                            value={path}
                            showNumerOnNoItems
                            onChange={(value) => {
                                setPath(value)
                            }}
                        />
                        <div className="reservas-screen__table__search-section">
                            <Search onChange={searchFilter} />
                            {path === "/all" && !searchValue && (
                                <>
                                    <CalendarButtons
                                        className="gastos-screen-header-controls"
                                        currMonth={date}
                                        setCurrMonth={setDate}
                                        currYear={year}
                                        setCurrYear={setYear}
                                        monthNames={options.map(({ label }) => label)}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    <div
                        className={`reservas-screen__table-wrapper${showGradient ? " reservas-screen__table-wrapper--show-gradient" : ""
                        }`}
                    >
                        <FlexibleTable
                            ref={tableRef}
                            tabIndex={0}
                            onHasScrollChange={setShowGradient}
                            onSelectedFilters={(value) => setOnFilterTable(value)}
                            tableItems={{
                                ...{
                                    headers: headers,
                                    rows: dataTable.map((table) => ({
                                        ...table,
                                    })),
                                },
                            }}
                            onSort={(value) => onSortData(value)}
                            emptyState={{
                                headerIcon: "BedFilled",
                                titile: "No se encontraron reservas",
                            }}
                            goTo={(id) => {
                                drawerCreate(id)
                            }}
                        ></FlexibleTable>
                    </div>
                </div>
            ) : (
                <div>
                    <div className="reservas-screen__table__filter-section">
                        <TabMenu
                            className="reservas-screen__table__tabs"
                            tabList={tabsValue}
                            value={path}
                            showNumerOnNoItems
                            onChange={(value) => {
                                setPath(value)
                            }}
                        />
                        <div className="reservas-screen__table__search-section">
                            <Search onChange={searchFilter} />
                            {path === "/all" && !searchValue && (
                                <>
                                    <CalendarButtons
                                        className="gastos-screen-header-controls"
                                        currMonth={date}
                                        setCurrMonth={setDate}
                                        currYear={year}
                                        setCurrYear={setYear}
                                        monthNames={options.map(({ label }) => label)}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    <div className="gastos-empty-state">
                        <EmptyState
                            title="No hay reservaciones"
                            subtitle="Comienza registrando tus reservas aquí"
                            headerIcon="BedFilled"
                        />
                    </div>
                </div>
            )}
            <DrawerSection
                visible={isDrawerOpen}
                id={selectReserva}
                onClose={() => toggleDrawerState(false)}
                onRefresh={() => {
                    const refetchFn = path === "/current" ? refetchDiaActual
                        : path === "/currentmonth" ? refetchMesActual
                        : refetch
                    refetchFn().then((result) => {
                        const updated = result.data?.reservas.find((r) => r.reserva_id === selectReserva)
                        if (updated) dispatch(selectReservation(updated))
                    }).catch(() => undefined)
                }}
            />
            {rolName !== RoleNames.valet && rolName !== RoleNames.mantenimiento && <AddButton />}
        </section>
    )
}

const CARD_TYPES: string[] = [TiposPagos.VisaOMastercard, TiposPagos.Amex]

const getMetodoPagoLabel = (
    pagos?: GetReservacionesTableQuery["reservas"][0]["pagos"]
): string => {
    const tipos = new Set<string>()
    pagos?.forEach((pago) => {
        pago?.detalles_pago?.forEach((detalle) => {
            if (detalle?.tipo_pago) {
                tipos.add(detalle.tipo_pago)
            }
        })
    })
    if (tipos.size === 0) return "N/A"
    if (tipos.size > 1) return "Mixto"
    const tipo = [...tipos][0]
    const method = Object.values(PAYMENT_METHODS).find((m) => m.value === tipo)
    return method?.label || tipo
}

const formatMetodoPago = (
    pagos?: GetReservacionesTableQuery["reservas"][0]["pagos"]
): JSX.Element | string => {
    const detalles: { tipo_pago: string; ultimos_digitos?: string | null }[] = []
    pagos?.forEach((pago) => {
        pago?.detalles_pago?.forEach((detalle) => {
            if (detalle?.tipo_pago) {
                detalles.push({ tipo_pago: detalle.tipo_pago, ultimos_digitos: detalle.ultimos_digitos })
            }
        })
    })

    if (detalles.length === 0) return "N/A"

    const tipos = [...new Set(detalles.map((d) => d.tipo_pago))]

    if (tipos.length > 1) {
        const lastDigits = [...new Set(detalles.filter((d) => d.ultimos_digitos).map((d) => d.ultimos_digitos))]
        return (
            <div style={{ display: "flex", flexDirection: "column" }}>
                <span>Mixto</span>
                {lastDigits.length > 0 && (
                    <span style={{ color: "var(--placeholder)", fontSize: "12px" }}>
                        {lastDigits.map((d, i) => `*${d}${i < lastDigits.length - 1 ? ", " : ""}`).join("")}
                    </span>
                )}
            </div>
        )
    }

    const tipo = tipos[0]
    const method = Object.values(PAYMENT_METHODS).find((m) => m.value === tipo)
    const label = method?.label || tipo

    if (CARD_TYPES.includes(tipo)) {
        const lastDigits = [...new Set(detalles.filter((d) => d.ultimos_digitos).map((d) => d.ultimos_digitos))]
        return (
            <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{label}</span>
                {lastDigits.length > 0 && (
                    <span style={{ color: "var(--placeholder)", fontSize: "12px" }}>
                        *{lastDigits.join(", *")}
                    </span>
                )}
            </div>
        )
    }

    return label
}

const formatTableState = ({
    statePago,
    fechaCancel,
    isNoShow,
}: {
    statePago: EstadoPago
    fechaCancel?: Date
    isNoShow: boolean
}) => {
    const pendiente = statePago === EstadoPago.PagoParcial || statePago === EstadoPago.SinPagos
    const fechaCancelExists = !isNaN(new Date(fechaCancel || "").getDate())

    const state = isNoShow
        ? "disabled"
        : fechaCancelExists
            ? "danger"
            : pendiente
                ? "warning"
                : statePago === EstadoPago.Pagado
                    ? "success"
                    : "success"

    const label = isNoShow
        ? "No show"
        : fechaCancelExists
            ? "Cancelada"
            : pendiente
                ? "Pendiente"
                : statePago === EstadoPago.Pagado
                    ? "Pagada"
                    : ""

    return (
        <div className="reservas-screen__table-cell__pago">
            <Badge label={label} state={state} />
        </div>
    )
}
const getPagoLabel = ({
    statePago,
    fechaCancel,
    isNoShow,
}: {
    statePago: EstadoPago
    fechaCancel?: Date
    isNoShow: boolean
}) => {
    const pendiente = statePago === EstadoPago.PagoParcial || statePago === EstadoPago.SinPagos
    const fechaCancelExists = !isNaN(new Date(fechaCancel || "").getDate())

    return isNoShow ? "No show" : fechaCancelExists ? "Cancelada" : pendiente ? "Pendiente" : "Pagada"
}

const formatTableStateAsignacion = ({
    idHabitacion,
    numHabitacion,
}: {
    idHabitacion?: string | null
    numHabitacion: string
}) => {
    return (
        <div className="reservas-screen__table-cell__pago">
            <div
                key={uuid()}
                className={cx({
                    "reservas-screen__table-cell__pago-text": true,
                    "reservas-screen__table-cell--cancelada": idHabitacion === null || idHabitacion === undefined,
                })}
            >
                {!idHabitacion && (
                    <Icon name="dot" color="red" width={10} height={10} style={{ marginRight: "12px" }} />
                )}
                {idHabitacion ? `#${numHabitacion}` : "Sin asignar"}
            </div>
        </div>
    )
}

const tableItems = () => {
    const { hotel_id } = useProfile()
    const { data } = useGetTiposHabitacionQuery({
        variables: { hotel_id: hotel_id },
    })

    // Definir una variable para almacenar los tipos de habitación

    const [tiposTipe, settiposTipe] = useState<{ value: string; valueToDisplay: string }[]>([])

    useEffect(() => {
        if (data && data?.tipo_habitaciones) {
            // Mapear los tipos de habitación desde la respuesta GraphQL
            settiposTipe(
                data?.tipo_habitaciones?.map((value) => ({
                    value: value?.nombre || "",
                    valueToDisplay: value?.nombre || "",
                }))
            )
        }
    }, [data])

    // Definir la estructura de la tabla
    const [tableItems, settableItems] = useState<
        {
            value: string
            sort?: boolean
            filterMenu?: { value: string; valueToDisplay: string }[]
            isFilterUnique?: boolean
        }[]
    >([])

    useEffect(() => {
        settableItems([
            {
                value: "Código",
            },
            {
                value: "Habitación",
                sort: true,
            },
            {
                value: "Origen",
            },
            {
                value: "Tipo",
                filterMenu: tiposTipe,
                isFilterUnique: false,
            },
            {
                value: "Huésped",
            },
            {
                value: "Estancia",
            },
            {
                value: "Tarifa",
            },
            {
                value: "Método de pago",
                filterMenu: [
                    { value: PAYMENT_METHODS.efectivo.label, valueToDisplay: PAYMENT_METHODS.efectivo.label },
                    { value: PAYMENT_METHODS.visaOMasterCard.label, valueToDisplay: PAYMENT_METHODS.visaOMasterCard.label },
                    { value: PAYMENT_METHODS.amex.label, valueToDisplay: PAYMENT_METHODS.amex.label },
                    { value: PAYMENT_METHODS.cortesia.label, valueToDisplay: PAYMENT_METHODS.cortesia.label },
                    { value: PAYMENT_METHODS.depositoOTransferencia.label, valueToDisplay: PAYMENT_METHODS.depositoOTransferencia.label },
                    { value: PAYMENT_METHODS.consumoInterno.label, valueToDisplay: PAYMENT_METHODS.consumoInterno.label },
                    { value: PAYMENT_METHODS.lovePoints.label, valueToDisplay: PAYMENT_METHODS.lovePoints.label },
                    { value: "Mixto", valueToDisplay: "Mixto" },
                ],
                isFilterUnique: false,
            },
            {
                value: "Pago",
                filterMenu: [
                    { value: "Pagada", valueToDisplay: "Pagada" },
                    { value: "Pendiente", valueToDisplay: "Pendiente" },
                    { value: "Cancelada", valueToDisplay: "Cancelada" },
                    { value: "No show", valueToDisplay: "No show" },
                ],
                isFilterUnique: false,
            },
        ])
    }, [tiposTipe])

    return tableItems
}
